"use client";

import { Divider, InputPrice, List, RadioGroupRoot } from "@dafiti/closet-ui";
import { useLocale, useTranslations } from "next-intl";

import { usePriceFilter } from "../../../hooks/usePriceFilter.hook";
import { useFilterContext } from "../../../providers";
import type { CustomPriceFacet } from "../../../types/filters.type";
import { FilterOptionListItem } from "../FilterOptionList";

interface Props {
  facet: CustomPriceFacet;
}

export function CustomPriceFilter({ facet }: Props) {
  const t = useTranslations("catalog");
  const locale = useLocale();
  const { getSelectedValues, onSelectionChange } = useFilterContext();
  const initialValues = getSelectedValues(facet.field);

  const {
    minValue,
    maxValue,
    selectedRangeValue,
    rangeOptions,
    handleMinChange,
    handleMaxChange,
    handleMinBlur,
    handleMaxBlur,
    selectRange,
  } = usePriceFilter(facet, initialValues, (values) =>
    onSelectionChange(facet.field, values),
  );

  return (
    <div className="flex flex-col gap-4" data-testid="custom-price-filter">
      <div className="flex flex-col gap-4 px-0">
        <span className="text-body-2 font-normal text-black text-center">
          {t("price.prompt")}
        </span>
        <div className="flex items-center gap-2">
          <InputPrice
            size="lg"
            label={t("price.min")}
            currency={facet.currency}
            locale={locale}
            precision={0}
            value={minValue}
            inputMode="numeric"
            onChange={(event) => handleMinChange(event.target.value)}
            onBlur={handleMinBlur}
            data-testid="price-filter-min"
          />
          <InputPrice
            size="lg"
            label={t("price.max")}
            currency={facet.currency}
            locale={locale}
            precision={0}
            value={maxValue}
            inputMode="numeric"
            labelClassName="left-auto right-4 origin-top-right"
            inputContainerClassName="[&_input]:text-right"
            onChange={(event) => handleMaxChange(event.target.value)}
            onBlur={handleMaxBlur}
            data-testid="price-filter-max"
          />
        </div>
      </div>

      <Divider className="bg-gray-200 mt-2" />

      <RadioGroupRoot
        value={selectedRangeValue}
        onValueChange={selectRange}
        data-testid="price-range-list"
      >
        <List as="div" className="divide-y-0">
          {rangeOptions.map((option) => (
            <FilterOptionListItem
              className="py-2"
              hideDivider={true}
              key={option.value}
              value={option.value}
              label={option.label}
              checked={selectedRangeValue === option.value}
              selector="Single"
              onToggle={() => undefined}
            />
          ))}
        </List>
      </RadioGroupRoot>
    </div>
  );
}
